import { useCallback, useMemo, useRef } from "react";
import { useMutation } from "@apollo/client";
import { message } from "antd";
import { PIN_LEADS, UNPIN_LEADS } from "../graphql/mutation/mutation";

/**
 * Pinned leads for the Activity Center lead list (EAI-1765).
 *
 * The server owns pins. They live per `(user, location)` and are never visible
 * to another user, so there is no client store and nothing persisted here — an
 * earlier revision kept them in redux-persist, which made them per-browser.
 *
 * Read path — all server-side, nothing is filtered here:
 *   - per-row state  -> `lead.isPinned`, stamped by `resolve_get_leads`
 *   - the Pinned tab -> the same `getLeads` with `isPinned: true`, so it
 *                       paginates, sorts and filters like any other tab, and
 *                       its `count` is the pin total in that scope
 *
 * There is deliberately no standalone pin total: `PaginatedLeads.pinnedCount`
 * was dropped, so how many pins a user holds is only knowable while the Pinned
 * tab is the active query.
 *
 * Pinning no longer reorders the list: `resolve_get_leads` dropped the
 * pin-first sort key, so a pinned lead sits wherever the caller's
 * orderBy/orderDirection puts it and `isPinned` is the only per-row signal.
 * Pins are also resolved during a search now, so the flag and the count stay
 * truthful with a search term active.
 */

const toIdArray = leadIds =>
  (Array.isArray(leadIds) ? leadIds : [leadIds])
    .map(id => Number(id))
    .filter(id => !Number.isNaN(id) && id > 0);

const usePinnedLeads = ({ leads = [], locationId = null, onChanged = () => {} } = {}) => {
  const [pinLeadsMutation, { loading: pinning }] = useMutation(PIN_LEADS);
  const [unpinLeadsMutation, { loading: unpinning }] = useMutation(UNPIN_LEADS);

  // Last known pin state per lead id. `getLeads` stamps every row it returns,
  // but rows reach the list by other routes too — a socket refresh that
  // prepends a lead, the new-lead popup, Lead Center's "apply to Activity
  // Center" — and those carry no `isPinned`. Learned from every response and
  // kept true by our own mutation results, so those rows still render the
  // right pin affordance.
  const knownPinsRef = useRef(new Map());
  (leads || []).forEach(lead => {
    if (lead?.id != null && lead.isPinned != null) {
      knownPinsRef.current.set(+lead.id, !!lead.isPinned);
    }
  });
  const rememberPins = (ids, pinned) =>
    (ids || []).forEach(id => knownPinsRef.current.set(+id, pinned));

  const isPinned = useCallback(
    lead => {
      if (lead == null) return false;
      const id = typeof lead === "object" ? lead?.id : lead;
      if (typeof lead === "object" && lead.isPinned != null) return !!lead.isPinned;
      const row = (leads || []).find(item => +item?.id === +id);
      if (row?.isPinned != null) return !!row.isPinned;
      return !!knownPinsRef.current.get(+id);
    },
    [leads]
  );

  // PIN ONLY, and REQUIRED there (Int!). The server refuses to guess the
  // rooftop, so we resolve it: the top-bar location when one is selected, else
  // the lead's own. Only a single concrete rooftop is valid — the "1,2,3"
  // all-locations form is not an Int, hence the per-lead fallback below.
  //
  // Never send it on unpin. There, location_id NARROWS the sweep to one
  // rooftop, and `remove_pins` then misses a lead that was pinned while scoped
  // to a different one — which the resolver itself calls out ("a lead pinned
  // while scoped to one rooftop cannot always be resolved back to it later").
  // The server's default, every permitted location, is the behaviour we want.
  const scopedLocationId = useMemo(() => {
    const parsed = Number(locationId);
    return !Number.isNaN(parsed) && parsed > 0 ? parsed : undefined;
  }, [locationId]);

  // Under "All locations" the top bar gives no single id, so fall back to the
  // lead's own location — the rooftop the user is looking at it in.
  const resolvePinLocationId = useCallback(
    leadOrId => {
      if (scopedLocationId) return scopedLocationId;
      const id = typeof leadOrId === "object" ? leadOrId?.id : leadOrId;
      const row =
        typeof leadOrId === "object" && leadOrId?.location?.id != null
          ? leadOrId
          : (leads || []).find(item => Number(item?.id) === Number(id));
      const fromRow = Number(row?.location?.id);
      return !Number.isNaN(fromRow) && fromRow > 0 ? fromRow : undefined;
    },
    [scopedLocationId, leads]
  );

  const pinLeads = useCallback(
    async (leadIds, lang = {}) => {
      const list = Array.isArray(leadIds) ? leadIds : [leadIds];
      const ids = toIdArray(list.map(item => (typeof item === "object" ? item?.id : item)));
      if (!ids.length) return false;
      // locationId is Int! — bail with a message rather than send null and
      // have the server reject the whole document.
      const locationIdForPin = resolvePinLocationId(list[0]);
      if (!locationIdForPin) {
        message.error(lang.somethingWentWrong || "Couldn't determine the lead's location");
        return false;
      }
      try {
        const { data } = await pinLeadsMutation({
          variables: { leadIds: ids, locationId: locationIdForPin },
        });
        const res = data?.pinLeads;
        if (res?.statusCode !== 200) {
          message.error(res?.message || lang.somethingWentWrong || "Something went wrong");
          return false;
        }
        // Idempotent by design: everything already pinned (or stale) comes back
        // in skippedIds. Only say "pinned" when something actually changed.
        //
        // The copy is ours, not the server's: it returns "1 lead pinned",
        // which is terse and can't be localised. Error paths still echo the
        // server, since those carry information we don't have.
        const pinned = (res.pinnedIds || []).length;
        if (pinned === 1) {
          message.success(lang.leadPinnedSuccess || "Lead pinned successfully");
        } else if (pinned > 1) {
          message.success(
            (lang.leadsPinnedSuccess || "__count__ leads pinned successfully").replace(
              "__count__",
              pinned
            )
          );
        } else if ((res.skippedIds || []).length) {
          message.info(lang.leadAlreadyPinned || "Lead is already pinned");
        }
        rememberPins(res.pinnedIds, true);
        onChanged(res);
        return pinned > 0;
      } catch (error) {
        message.error(lang.somethingWentWrong || "Something went wrong");
        return false;
      }
    },
    [pinLeadsMutation, resolvePinLocationId, onChanged]
  );

  const unpinLeads = useCallback(
    async (leadIds, lang = {}) => {
      const ids = toIdArray(leadIds);
      if (!ids.length) return false;
      try {
        const { data } = await unpinLeadsMutation({
          // Never send unpinAll alongside leadIds — the server 400s when both
          // (or neither) are set. locationId is deliberately omitted so the
          // unpin sweeps every permitted rooftop; see scopedLocationId.
          variables: { leadIds: ids },
        });
        const res = data?.unpinLeads;
        if (res?.statusCode !== 200) {
          message.error(res?.message || lang.somethingWentWrong || "Something went wrong");
          return false;
        }
        // A 200 that removed nothing means the id wasn't pinned in any
        // rooftop. Say so — silence here reads as "the button is broken".
        const unpinned = (res.unpinnedIds || []).length;
        if (unpinned === 1) {
          message.success(lang.leadUnpinnedSuccess || "Lead unpinned successfully");
        } else if (unpinned > 1) {
          message.success(
            (lang.leadsUnpinnedSuccess || "__count__ leads unpinned successfully").replace(
              "__count__",
              unpinned
            )
          );
        } else {
          message.info(lang.nothingToUnpin || "Nothing to unpin");
        }
        rememberPins(res.unpinnedIds, false);
        onChanged(res);
        return (res.unpinnedIds || []).length > 0;
      } catch (error) {
        message.error(lang.somethingWentWrong || "Something went wrong");
        return false;
      }
    },
    [unpinLeadsMutation, onChanged]
  );

  const unpinAll = useCallback(
    async (lang = {}) => {
      try {
        const { data } = await unpinLeadsMutation({
          // leadIds omitted deliberately: mutually exclusive with unpinAll.
          // locationId omitted too, so this clears every permitted rooftop —
          // narrowing it leaves pins behind in the other rooftops, which
          // reappear the moment the user switches location and reads as the
          // button having done nothing. See scopedLocationId.
          variables: { unpinAll: true },
        });
        const res = data?.unpinLeads;
        if (res?.statusCode !== 200) {
          message.error(res?.message || lang.somethingWentWrong || "Something went wrong");
          return false;
        }
        message.success(lang.allLeadsUnpinnedSuccess || "All leads unpinned successfully");
        knownPinsRef.current.clear();
        onChanged(res);
        return true;
      } catch (error) {
        message.error(lang.somethingWentWrong || "Something went wrong");
        return false;
      }
    },
    [unpinLeadsMutation, onChanged]
  );

  const togglePin = useCallback(
    (lead, lang = {}) => {
      const id = typeof lead === "object" ? lead?.id : lead;
      // Pass the row (not just the id) into pinLeads so it can read
      // `location.id` when the top bar is on "All locations".
      return isPinned(lead) ? unpinLeads([id], lang) : pinLeads([lead], lang);
    },
    [isPinned, pinLeads, unpinLeads]
  );

  return {
    isPinned,
    pinLeads,
    unpinLeads,
    unpinAll,
    togglePin,
    pinMutationLoading: pinning || unpinning,
  };
};

export default usePinnedLeads;
